import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { jwtDecode } from "jwt-decode";
import {
    CognitoIdentityProviderClient,
    GetUserCommand,
} from "@aws-sdk/client-cognito-identity-provider"; // ES Modules import
import { clearTokens, getAccessToken, isTokenValid } from "../../utils/tokenUtils";
import { fetchFullProfile } from "./profileSlice";
import { RootState } from "../../store/store";

interface JwtPayload {
    sub: string; // Cognito uses 'sub' for userId
    email: string;
    token_use: string;
    exp: number;
    "cognito:groups"?: string[]; // Add groups claim
}

interface UserData {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    emailVerified?: boolean;
    groups?: string[]; // Add groups field
    linkedinURL?: string;
}

interface CognitoUserAttribute {
    Name: string;
    Value: string;
}

interface CognitoResponse {
    UserAttributes: CognitoUserAttribute[];
    Username: string;
}

export interface AuthState {
    isAuthenticated: boolean;
    token: string | null;
    user: UserData | null;
    loading: boolean;
    error: string | null;
}

const initialState: AuthState = {
    isAuthenticated: false,
    token: null,
    user: null,
    loading: false,
    error: null,
};

export const loginAsync = createAsyncThunk("auth/loginAsync", async (token: string) => {
    return token;
});

// Check if user has a valid token and initialize the state
export const initAuth = createAsyncThunk("auth/initAuth", async (_, { dispatch }) => {
    try {
        // Check if we have a valid token
        if (isTokenValid()) {
            const token = getAccessToken();
            // If token exists and is valid, set auth state
            if (token) {
                // Set token in redux
                await dispatch(loginAsync(token));

                try {
                    // Fetch user profile data
                    await dispatch(fetchUserProfile(token));
                    dispatch(fetchFullProfile(token));
                } catch (profileError) {
                    console.error(
                        "Error fetching profile, but keeping user logged in:",
                        profileError,
                    );
                    // Even if profile fetch fails, we keep the user logged in
                    // as long as they have a valid token
                }

                return { success: true };
            }
        } else {
            // Clear any invalid tokens
            clearTokens();
        }

        return { success: false };
    } catch (error) {
        console.error("Error during auth initialization:", error);
        return { success: false };
    }
});

// Update the fetchUserProfile thunk
export const fetchUserProfile = createAsyncThunk(
    "auth/fetchUserProfile",
    async (token: string, { rejectWithValue }) => {
        try {
            const decodedToken = jwtDecode<JwtPayload>(token);
            const userId = decodedToken.sub;
            const groups = decodedToken["cognito:groups"] || []; // Extract groups

            if (!userId) {
                throw new Error("Invalid token: userId not found");
            }

            const cognitoClient = new CognitoIdentityProviderClient({
                region: "ca-central-1",
            });

            const command = new GetUserCommand({
                AccessToken: token,
            });

            const response = await cognitoClient.send(command);

            // Transform Cognito response to UserData format
            const attributes = response.UserAttributes || [];
            const userData: UserData = {
                id: userId,
                email: "",
                firstName: "",
                lastName: "",
                emailVerified: false,
                groups: groups, // Include groups here
                linkedinURL: "", // Initialize as empty
            };

            // Map Cognito attributes to UserData
            attributes.forEach((attr) => {
                switch (attr.Name) {
                    case "email":
                        userData.email = attr.Value || "";
                        break;
                    case "given_name":
                        userData.firstName = attr.Value || "";
                        break;
                    case "family_name":
                        userData.lastName = attr.Value || "";
                        break;
                    case "email_verified":
                        userData.emailVerified = attr.Value === "true";
                        break;
                }
            });

            return userData;
        } catch (error: any) {
            return rejectWithValue(error.message || "Failed to fetch user profile");
        }
    },
);

const authSlice = createSlice({
    name: "auth",
    initialState,
    reducers: {
        logout(state) {
            state.isAuthenticated = false;
            state.token = null;
            state.user = null;

            // Clear all tokens from storage (both localStorage and cookies)
            clearTokens();
        },
        setUser(state, action: PayloadAction<UserData>) {
            state.user = action.payload;
        },
        setUserLinkedinURL(state, action: PayloadAction<string | undefined>) {
            if (state.user) {
                state.user.linkedinURL = action.payload;
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(loginAsync.fulfilled, (state, action) => {
                state.isAuthenticated = true;
                state.token = action.payload;
                state.error = null;
            })
            .addCase(loginAsync.rejected, (state) => {
                state.isAuthenticated = false;
                state.token = null;
                state.error = "Login failed";
            })
            .addCase(fetchUserProfile.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchUserProfile.fulfilled, (state, action) => {
                state.loading = false;
                state.isAuthenticated = true;
                state.user = action.payload;
                state.error = null;
            })
            .addCase(fetchUserProfile.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            .addCase(initAuth.pending, (state) => {
                state.loading = true;
            })
            .addCase(initAuth.fulfilled, (state, action) => {
                state.loading = false;
                // Auth state is already set by loginAsync and fetchUserProfile thunks
            })
            .addCase(initAuth.rejected, (state) => {
                state.loading = false;
                state.isAuthenticated = false;
                clearTokens(); // Clear tokens if initialization fails
            });
    },
});

export const { logout, setUser, setUserLinkedinURL } = authSlice.actions;

// Selector to get the authentication status
export const selectIsAuthenticated = (state: RootState) => state.auth.isAuthenticated;

// Selector to get the authentication token
export const selectAuthToken = (state: RootState) => state.auth.token;

// Selector to get the current user data
export const selectCurrentUser = (state: RootState) => state.auth.user;

export default authSlice.reducer;
